import { ref } from 'vue'
import { defineStore } from 'pinia'
import { Base64 } from 'js-base64'

import { jwtDecode } from 'jwt-decode'

import { type AuthState, AuthenticationStatus } from '../models/types'

const REFRESH_KEY = 'vigo.ot.auth.refresh'
const CLIENT_ID_KEY = 'vigo.ot.auth.clientid'
const NONCE_KEY = 'vigo.ot.auth.nonce'
const ROUTE_KEY = 'vigo.ot.auth.route'

const REFRESH_BEFORE = 300

export const useAuthStore = defineStore('auth', () => {
  const AUTH_PREFIX = 'Vigoauth '

  const applicationActive = ref(false)

  const status = ref<AuthenticationStatus>()

  const idToken = ref<Record<string, unknown> | null | undefined>()

  let authState = {} as AuthState

  let keepaliveTimer: ReturnType<typeof setTimeout> | undefined = undefined

  let hidden = false

  let keepaliveRunning = false

  let refreshPromise: Promise<Response> | undefined = undefined

  function clear(newStatus: AuthenticationStatus) {
    const newState = {} as AuthState

    newState.accessToken = null
    newState.refreshToken = null
    newState.idToken = null
    newState.status = newStatus

    window.sessionStorage.removeItem(REFRESH_KEY)
    window.sessionStorage.removeItem(NONCE_KEY)
    window.sessionStorage.removeItem(CLIENT_ID_KEY)

    authState = newState

    removeSessionKeepalive()
  }

  function setLogout() {
    clear(AuthenticationStatus.LOGOUT)
  }

  function setError() {
    clear(AuthenticationStatus.ERROR)
  }

  function setTokens(payload: any) {
    const newState = {} as AuthState
    newState.status = AuthenticationStatus.AUTHENTICATED

    newState.accessToken = payload.access_token
    newState.refreshToken = payload.refresh_token
    window.sessionStorage.setItem(REFRESH_KEY, payload.refresh_token)
    newState.idToken = jwtDecode(payload.id_token)
    idToken.value = newState.idToken
    newState.expiresEpochMs = Date.now() + Math.max(payload.expires_in - REFRESH_BEFORE, 30) * 1000

    authState = newState

    if (status.value !== AuthenticationStatus.AUTHENTICATED) {
      status.value = authState.status

      addSessionKeepalive()

      window.addEventListener('beforeunload', () => {
        // visibilitychange is fired on F5 resulting in duplicate token calls. Remove keepalive when unloading document
        removeSessionKeepalive()
      })

      document.addEventListener('visibilitychange', () => {
        changePageVisibility(document.hidden)
      })
    }
  }

  async function fetchTokens(code: string, state: string) {
    window.sessionStorage.removeItem(REFRESH_KEY)
    const nonce = window.sessionStorage.getItem(NONCE_KEY)!
    const clientId = window.sessionStorage.getItem(CLIENT_ID_KEY)!
    if (!clientId || !nonce) {
      newAuth('default')

      return
    }
    const redirectUri = `${window.location.origin}${import.meta.env.BASE_URL}client-auth/response`

    const params = new URLSearchParams()
    params.set('client_id', clientId)
    params.set('redirect_uri', redirectUri)

    const auth = AUTH_PREFIX.concat(Base64.encode(`${nonce}:${state}`))

    const authParamsResponse = await fetch(
      `${import.meta.env.BASE_URL}v1/server-auth/tokenParams?${params.toString()}`,
      {
        headers: {
          Authorization: auth
        }
      }
    )
    window.sessionStorage.removeItem(NONCE_KEY)
    window.sessionStorage.removeItem(CLIENT_ID_KEY)

    if (!authParamsResponse.ok) {
      setError()
      throw new Error('Could not retrieve token parameters')
    }
    const authParams = await authParamsResponse.json()

    const tokenParams = new URLSearchParams()
    tokenParams.set('client_id', clientId)
    tokenParams.set('redirect_uri', redirectUri)

    tokenParams.set('code', code)
    tokenParams.set('grant_type', 'authorization_code')
    tokenParams.set('code_verifier', authParams.code_verifier)

    try {
      const response = await fetch(`${import.meta.env.BASE_URL}v1/server-auth/token`, {
        method: 'POST',
        body: tokenParams,
        headers: {
          Authorization: auth
        }
      })

      if (!response.ok) {
        setError()
        throw new Error('Could not retrieve token')
      }
      setTokens(await response.json())
    } catch (_error) {
      setError()
    }
  }

  async function refreshTokens() {
    if (refreshPromise) {
      await refreshPromise
      return
    }
    const data = new URLSearchParams()
    data.set('refresh_token', authState.refreshToken!)
    data.set('grant_type', 'refresh_token')
    try {
      refreshPromise = fetch(`${import.meta.env.BASE_URL}v1/server-auth/token`, {
        method: 'POST',
        body: data
      })
      const response = await refreshPromise
      if (response.ok) {
        setTokens(await response.json())
      } else if (response.status != 503 && response.status != 504) {
        setLogout()
        await initateLogout()
      }
    } finally {
      refreshPromise = undefined
    }
  }

  async function newAuth(idp: string) {
    const random = new Uint8Array(32)
    crypto.getRandomValues(random)

    const nonce = Base64.fromUint8Array(random, true)

    window.sessionStorage.removeItem(REFRESH_KEY)
    window.sessionStorage.setItem(NONCE_KEY, nonce)

    const auth = AUTH_PREFIX.concat(
      Base64.encode(
        `${idp}:${window.location.origin}${import.meta.env.BASE_URL}client-auth/response`
      )
    )

    // We add an Authorization header to stop cross-origin requests from a browser (at least if nobody messes up the server)
    const authParamsResponse = await fetch(
      `${import.meta.env.BASE_URL}v1/server-auth/auth-params?nonce=${nonce}`,
      {
        method: 'GET',
        headers: {
          Authorization: auth
        }
      }
    )
    if (!authParamsResponse.ok) {
      setError()
      throw new Error('Could not retrieve auth params')
    }
    const authParams = await authParamsResponse.json()

    const params = new URLSearchParams()
    window.sessionStorage.setItem(CLIENT_ID_KEY, authParams.client_id)

    params.set('client_id', authParams.client_id)
    params.set('response_type', 'code')
    params.set('state', authParams.state)
    params.set('nonce', authParams.nonce)
    params.set('code_challenge', authParams.code_challenge)
    params.set('code_challenge_method', authParams.code_challenge_method)
    params.set(
      'redirect_uri',
      `${window.location.origin}${import.meta.env.BASE_URL}client-auth/response`
    )
    params.set('scope', 'openid profile')

    window.location.replace(
      `${import.meta.env.BASE_URL}v1/server-auth/authorize?${params.toString()}`
    )
  }

  async function authInit() {
    if (authState?.status === AuthenticationStatus.AUTHENTICATED) {
      throw new Error('Already authenticated and trying to initiate a new one')
    }
    const params = new URLSearchParams(window.location.search)
    if (
      params.has('code') &&
      params.has('state') &&
      window.location.pathname === `${import.meta.env.BASE_URL}client-auth/response`
    ) {
      return fetchTokens(params.get('code')!, params.get('state')!)
    }
    if (
      params.has('error') &&
      window.location.pathname === `${import.meta.env.BASE_URL}client-auth/response`
    ) {
      setError()
      return
    }

    const route = params.get('route')
    if (route) {
      window.sessionStorage.setItem(ROUTE_KEY, route)
    } else {
      window.sessionStorage.removeItem(ROUTE_KEY)
    }

    authState.refreshToken = window.sessionStorage.getItem(REFRESH_KEY)
    if (authState.refreshToken) {
      return refreshTokens()
    }
    if (!authState.accessToken) {
      return newAuth(params.has('idp') ? params.get('idp')! : 'default')
    }
  }

  async function initateLogout() {
    const postLogoutLocation = `${window.location.origin}/vigo-ot/`
    if (authState.status == AuthenticationStatus.AUTHENTICATED) {
      const logoutParams = new URLSearchParams()
      logoutParams.set('refresh_token', authState.refreshToken!)
      logoutParams.set('redirect_uri', postLogoutLocation)

      clear(AuthenticationStatus.LOGGING_OUT)
      const response = await fetch(`${import.meta.env.BASE_URL}v1/server-auth/endsession`, {
        method: 'POST',
        body: logoutParams
      })
      if (response.ok) {
        window.location.replace(response.headers.get('location')!)
      } else {
        window.location.replace(logoutParams.get('redirect_uri')!)
      }
    } else {
      clear(AuthenticationStatus.LOGGING_OUT)
      window.location.replace(postLogoutLocation)
    }
  }

  async function keepalive(forceRefresh: boolean) {
    try {
      if (keepaliveRunning) {
        return
      }
      keepaliveRunning = true

      const timedOut =
        !authState ||
        authState?.status !== AuthenticationStatus.AUTHENTICATED ||
        Date.now() > authState?.expiresEpochMs + REFRESH_BEFORE * 1000

      if (timedOut) {
        setLogout()
        window.location.replace('/')
        return
      }
      await getAccessToken(forceRefresh)
    } finally {
      keepaliveRunning = false
    }
  }

  async function changePageVisibility(newHidden: boolean) {
    if (hidden === newHidden) {
      return
    }
    if (newHidden) {
      removeSessionKeepalive()
    } else if (!newHidden) {
      addSessionKeepalive()
    }
    if (refreshPromise) {
      return
    }
    hidden = newHidden
    const iat = (authState?.idToken?.iat as number) ?? Date.now() / 1000
    await keepalive(iat < Date.now() / 1000 - 30)
  }

  function addSessionKeepalive() {
    if (keepaliveTimer) {
      return
    }
    applicationActive.value = true
    keepaliveTimer = setInterval(() => keepalive(false), 2500)
  }

  function removeSessionKeepalive() {
    if (!keepaliveTimer) {
      return
    }
    applicationActive.value = false
    clearInterval(keepaliveTimer)
    keepaliveTimer = undefined
  }

  function getInitialRoute(): string | undefined {
    const route = window.sessionStorage.getItem(ROUTE_KEY)
    window.sessionStorage.removeItem(ROUTE_KEY)

    return route ? route : window.location.pathname.includes('/client-auth/') ? '/' : undefined
  }

  async function getAccessToken(forceRefresh: boolean): Promise<string | null | undefined> {
    if (authState.refreshToken && (forceRefresh || authState.expiresEpochMs < Date.now())) {
      await refreshTokens()
      return authState.accessToken
    }
    return authState?.accessToken
  }

  return {
    status,
    authInit,
    initateLogout,
    getInitialRoute,
    getAccessToken,
    idToken,
    applicationActive
  }
})
